import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Download, Zap } from 'lucide-react';
import NeuralCanvas from './NeuralCanvas';

const STATS = [
  { v: 8, suffix: '+', label: 'Years Experience' },
  { v: 15, suffix: '+', label: 'Fortune 500 Clients' },
  { v: 1000, suffix: '+', label: 'Professionals Trained' },
  { v: 10, suffix: '+', label: 'Industries Covered' },
];

function CountUp({ to, suffix = '' }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let started = false;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) {
          started = true;
          const dur = 1400;
          const start = performance.now();
          const step = (t) => {
            const p = Math.min(1, (t - start) / dur);
            setN(Math.floor(p * to));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      });
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to]);
  return <span ref={ref}>{n.toLocaleString()}{suffix}</span>;
}

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16 halftone halftone-soft">
      <NeuralCanvas />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(60% 50% at 20% 30%, rgba(0,245,212,0.08) 0%, transparent 70%), radial-gradient(40% 40% at 85% 80%, rgba(245,158,11,0.06) 0%, transparent 70%)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="flex items-center gap-2 mb-8"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-teal/30 bg-teal/5">
            <Zap size={12} className="text-teal" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-teal">
              Senior Data Scientist · AI Engineering Leader · Corporate Trainer
            </span>
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="font-display font-black text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter max-w-5xl"
        >
          I Turn AI Hype Into{' '}
          <span className="text-teal">Real Skills</span>{' '}
          Your Team<br className="hidden md:block" /> Actually Uses.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed"
        >
          8+ years building production ML systems for Fortune 500 companies. Now training the
          next wave of AI practitioners across India's top enterprises.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={() => scrollTo('#contact')}
            data-testid="hero-cta-primary"
            className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-teal text-[#0a0a0f] font-mono text-sm uppercase tracking-[0.15em] font-bold rounded-full border-2 border-teal hover:bg-white hover:border-white transition-colors"
          >
            Book a Corporate Training
            <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => scrollTo('#training')}
            data-testid="hero-cta-secondary"
            className="group inline-flex items-center justify-center gap-2 px-7 py-4 border-2 border-white/20 text-white font-mono text-sm uppercase tracking-[0.15em] font-medium rounded-full hover:border-teal hover:text-teal transition-colors"
          >
            <Download size={15} />
            Download Brochure
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {STATS.map((s) => (
            <div
              key={s.label}
              data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
              className="glass rounded-2xl px-5 py-5"
            >
              <div className="font-display font-black text-3xl md:text-4xl text-white tracking-tight">
                <CountUp to={s.v} suffix={s.suffix} />
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
